// src/ventes/factures/NouvelleFacture.jsx
import React, { useState, useEffect } from "react";
import {
    Card, Row, Col, Form, Button, Table, Spinner,
    Modal, InputGroup, Badge
} from "react-bootstrap";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency } from "../utils/formatters";
import { TVA_TAUX, CSS_TAUX } from "../utils/constantes";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 4000 }) => {
    if (!show) return null;

    const toastConfig = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    };
    const config = toastConfig[type] || toastConfig.info;

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom">
                    <p className="mb-0">{message}</p>
                </div>
                <div
                    className="toast-progress"
                    style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}
                ></div>
            </div>
        </div>
    );
};

/* ============================================================
   MODAL — Ajout d'un service libre
   ============================================================ */
const ModalServiceLibre = ({ show, onClose, onAdd, taxes }) => {
    const [libelle, setLibelle] = useState('');
    const [montant, setMontant] = useState(0);
    const [unite, setUnite] = useState('FORFAIT');
    const [erreur, setErreur] = useState('');

    const reset = () => { setLibelle(''); setMontant(0); setUnite('FORFAIT'); setErreur(''); };

    const handleClose = () => { reset(); onClose(); };

    const handleSubmit = () => {
        if (!libelle.trim()) {
            setErreur("Le libellé du service est obligatoire");
            return;
        }
        if (!montant || montant <= 0) {
            setErreur("Le montant doit être supérieur à 0");
            return;
        }
        onAdd({
            designation: libelle.trim(),
            prix_unitaire_ht: parseFloat(montant),
            unite_facturation: unite,
            tva_taux: taxes?.tva_active ? taxes.tva_taux : 0,
            css_taux: taxes?.css_active ? taxes.css_taux : 0
        });
        reset();
        onClose();
    };

    return (
        <Modal show={show} onHide={handleClose} centered className="product-modal">
            <Modal.Header closeButton className="modal-header-gradient">
                <Modal.Title className="text-white">
                    <i className="ri-service-line me-2"></i>
                    Ajouter un service libre
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="px-4 pt-4 pb-2">
                <p className="text-muted small mb-3">
                    Utilisez ce formulaire pour facturer une prestation qui n'est pas enregistrée dans
                    votre catalogue. Elle ne sera pas ajoutée au stock ni au catalogue des articles.
                </p>

                {erreur && (
                    <div className="alert alert-danger py-2 px-3 small mb-3">{erreur}</div>
                )}

                <Form.Group className="mb-3">
                    <Form.Label className="form-label-modern">
                        Libellé du service <span className="text-danger">*</span>
                    </Form.Label>
                    <InputGroup className="input-modern">
                        <InputGroup.Text><i className="ri-text"></i></InputGroup.Text>
                        <Form.Control
                            type="text"
                            value={libelle}
                            onChange={(e) => setLibelle(e.target.value)}
                            placeholder="Ex: Forfait maintenance annuelle"
                            autoFocus
                        />
                    </InputGroup>
                </Form.Group>

                <Row className="g-3">
                    <Col md={7}>
                        <Form.Group>
                            <Form.Label className="form-label-modern">
                                Montant HT <span className="text-danger">*</span>
                            </Form.Label>
                            <InputGroup className="input-modern">
                                <InputGroup.Text><i className="ri-money-dollar-circle-line"></i></InputGroup.Text>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={montant}
                                    onChange={(e) => setMontant(parseFloat(e.target.value) || 0)}
                                />
                                <InputGroup.Text className="bg-light">FCFA</InputGroup.Text>
                            </InputGroup>
                        </Form.Group>
                    </Col>
                    <Col md={5}>
                        <Form.Group>
                            <Form.Label className="form-label-modern">Unité</Form.Label>
                            <InputGroup className="input-modern">
                                <InputGroup.Text><i className="ri-scales-line"></i></InputGroup.Text>
                                <Form.Select value={unite} onChange={(e) => setUnite(e.target.value)}>
                                    <option value="FORFAIT">Forfait</option>
                                    <option value="JOUR">Jour</option>
                                    <option value="HEURE">Heure</option>
                                    <option value="MOIS">Mois</option>
                                    <option value="PRESTATION">Prestation</option>
                                    <option value="INTERVENTION">Intervention</option>
                                </Form.Select>
                            </InputGroup>
                        </Form.Group>
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer className="border-0 pb-4 px-4">
                <Button variant="outline-secondary" onClick={handleClose} className="btn-modern-outline">
                    Annuler
                </Button>
                <Button onClick={handleSubmit} className="btn-modern-primary">
                    <i className="ri-add-line me-1"></i> Ajouter au tableau
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function NouvelleFacture() {
    const navigate = useNavigate();
    const location = useLocation();
    const [loadingDevis, setLoadingDevis] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [clients, setClients] = useState([]);
    const [produits, setProduits] = useState([]);
    const [showProduitModal, setShowProduitModal] = useState(false);
    const [showServiceLibreModal, setShowServiceLibreModal] = useState(false);
    const [toast, setToast] = useState({ show: false, type: 'success', title: '', message: '' });

    const [numerotation, setNumerotation] = useState(null);
    const [numeroFacture, setNumeroFacture] = useState('');

    const queryParams = new URLSearchParams(location.search);
    const devisId = queryParams.get('devis');
    const clientId = queryParams.get('client');

    const [facture, setFacture] = useState({
        id_client: clientId || '',
        date_facture: new Date().toISOString().split('T')[0],
        date_echeance: '',
        remise_globale: 0,
        avance_reglement: 0,
        notes: '',
        lignes: []
    });

    const [taxes, setTaxes] = useState({
        tva_taux: 18,
        css_taux: 1,
        tva_active: true,
        css_active: true
    });

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: 'success', title: '', message: '' });

    useEffect(() => {
        fetchClients();
        fetchProduits();
        fetchNumerotation();
        fetchTaxes();
        if (devisId) chargerDevis(devisId);
    }, [devisId]);

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchTaxes = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=taxes`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                if (result.data) setTaxes(result.data);
            }
        } catch (err) {
            console.error("Erreur chargement taxes", err);
        }
    };

    const fetchNumerotation = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/factures.php?action=prochain_numero`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                const data = result.data;
                setNumerotation(data.config);
                let numero = data.config.facture_prefix;
                if (data.config.facture_annee) numero += new Date().getFullYear();
                if (data.config.facture_mois) numero += ('0' + (new Date().getMonth() + 1)).slice(-2);
                numero += data.prochain.toString().padStart(data.config.facture_chiffres, '0');
                setNumeroFacture(numero);
            }
        } catch (err) {
            console.error("Erreur", err);
        }
    };

    const chargerDevis = async (id) => {
        try {
            setLoadingDevis(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/devis.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error("Erreur chargement du devis");
            const result = await response.json();
            const devisData = result.data;
            if (devisData) {
                setFacture({
                    id_client: devisData.id_client,
                    date_facture: new Date().toISOString().split('T')[0],
                    date_echeance: devisData.date_echeance || '',
                    remise_globale: devisData.remise_globale || 0,
                    avance_reglement: 0,
                    notes: `Facture issue du devis ${devisData.numero_proforma}\n${devisData.notes || ''}`,
                    lignes: devisData.lignes?.map(ligne => {
                        const isService = ligne.type_article === 'service' || !ligne.id_produit;
                        return {
                            id_produit: ligne.id_produit || null,
                            reference: ligne.reference || '',
                            designation: ligne.designation,
                            type_article: ligne.type_article || (ligne.id_produit ? 'produit' : 'service'),
                            unite_facturation: ligne.unite_facturation || null,
                            quantite: isService ? 1 : ligne.quantite,
                            prix_unitaire_ht: ligne.prix_unitaire_ht,
                            tva_taux: ligne.tva_taux || TVA_TAUX,
                            css_taux: ligne.css_taux || CSS_TAUX,
                            remise: ligne.remise || 0
                        };
                    }) || []
                });
            }
        } catch (err) {
            console.error("Erreur chargement devis:", err);
            setError("Erreur lors du chargement du devis");
        } finally {
            setLoadingDevis(false);
        }
    };

    const fetchClients = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/clients.php`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                setClients(result.data || []);
                if (clientId && result.data) {
                    const clientExists = result.data.some(c => c.id_client == clientId);
                    if (!clientExists) setError("Le client spécifié n'existe pas");
                }
            }
        } catch (err) {
            console.error("Erreur chargement clients", err);
        }
    };

    const fetchProduits = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/produits.php?include_stock=true`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                const produitsAvecStock = (result.data || []).map(p => ({
                    ...p,
                    type_article: p.type_article || 'produit',
                    stock_reel: p.type_article === 'service' ? null : (p.stock_reel || 0),
                    seuil_alerte: p.seuil_alerte || 5
                }));
                setProduits(produitsAvecStock);
            }
        } catch (err) {
            console.error("Erreur chargement produits", err);
        }
    };

    /* ============================================================
       GESTION DES LIGNES
       ============================================================ */
    const ajouterLigne = (produit) => {
        const isService = produit.type_article === 'service';
        const nouvelleLigne = {
            id_produit: produit.id_produit,
            reference: produit.reference,
            designation: produit.designation,
            type_article: produit.type_article || 'produit',
            unite_facturation: produit.unite_facturation || null,
            quantite: 1,
            prix_unitaire_ht: produit.prix_vente_ht,
            tva_taux: taxes.tva_active ? taxes.tva_taux : 0,
            css_taux: taxes.css_active ? taxes.css_taux : 0,
            remise: 0
        };
        setFacture({ ...facture, lignes: [...facture.lignes, nouvelleLigne] });
        setShowProduitModal(false);
        showToast('success',
            isService ? 'Service ajouté' : 'Produit ajouté',
            isService
                ? 'La prestation a été ajoutée à la facture'
                : 'Le produit a été ajouté à la facture');
    };

    /**
     * Ajout d'un service libre (non catalogué)
     */
    const ajouterServiceLibre = ({ designation, prix_unitaire_ht, unite_facturation, tva_taux, css_taux }) => {
        const nouvelleLigne = {
            id_produit: null,
            reference: 'LIBRE',
            designation: designation,
            type_article: 'service',
            unite_facturation: unite_facturation,
            quantite: 1,
            prix_unitaire_ht: prix_unitaire_ht,
            tva_taux: tva_taux,
            css_taux: css_taux,
            remise: 0,
            est_libre: true
        };
        setFacture({ ...facture, lignes: [...facture.lignes, nouvelleLigne] });
        showToast('success', 'Service libre ajouté', `"${designation}" a été ajouté à la facture`);
    };

    const supprimerLigne = (index) => {
        const nouvellesLignes = facture.lignes.filter((_, i) => i !== index);
        setFacture({ ...facture, lignes: nouvellesLignes });
        showToast('info', 'Ligne supprimée', 'La ligne a été retirée');
    };

    const updateLigne = (index, champ, valeur) => {
        const nouvellesLignes = [...facture.lignes];
        if (champ === 'quantite' && nouvellesLignes[index].type_article === 'service') {
            return;
        }
        nouvellesLignes[index][champ] = valeur;
        setFacture({ ...facture, lignes: nouvellesLignes });
    };

    /* ============================================================
       CALCUL DES TOTAUX
       ============================================================ */
    const calculerTotaux = () => {
        let total_ht = 0;
        let total_tva = 0;
        let total_css = 0;

        facture.lignes.forEach(ligne => {
            const montant_ligne_ht = ligne.quantite * ligne.prix_unitaire_ht * (1 - (ligne.remise || 0) / 100);
            total_ht += montant_ligne_ht;
            if (taxes.tva_active) total_tva += montant_ligne_ht * (ligne.tva_taux || taxes.tva_taux) / 100;
            if (taxes.css_active) total_css += montant_ligne_ht * (ligne.css_taux || taxes.css_taux) / 100;
        });

        const total_ttc = total_ht + total_tva + total_css;
        const net_a_payer = total_ttc - (facture.remise_globale || 0) - (facture.avance_reglement || 0);
        return { total_ht, total_tva, total_css, total_ttc, net_a_payer };
    };

    const totaux = calculerTotaux();

    /* ============================================================
       SOUMISSION
       ============================================================ */
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!facture.id_client) {
            showToast('error', 'Champ requis', 'Veuillez sélectionner un client');
            return;
        }
        if (facture.lignes.length === 0) {
            showToast('error', 'Champ requis', 'Veuillez ajouter au moins un produit ou service');
            return;
        }
        setSaving(true);
        try {
            const userStr = localStorage.getItem("user");
            if (!userStr) {
                showToast('error', 'Session', 'Utilisateur non connecté');
                return;
            }
            const user = JSON.parse(userStr);
            const id_utilisateur = user.id || user.id_utilisateur;
            if (!id_utilisateur) {
                showToast('error', 'Utilisateur', 'ID utilisateur non trouvé');
                return;
            }
            const token = localStorage.getItem("token");
            const payload = {
                action: 'create',
                ...facture,
                numero_facture: numeroFacture,
                id_utilisateur: id_utilisateur
            };
            if (devisId) payload.id_proforma = devisId;

            const response = await fetch(`${API_BASE_URL}/factures.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });
            const result = await response.json();
            if (result.success) {
                showToast('success', 'Facture créée', 'La facture a été enregistrée avec succès');
                setTimeout(() => navigate('/ventes/factures/liste'), 2000);
            } else {
                showToast('error', 'Erreur', result.message || "Erreur lors de la création");
            }
        } catch (err) {
            console.error("Erreur:", err);
            showToast('error', 'Erreur de connexion', "Impossible de contacter le serveur");
        } finally {
            setSaving(false);
        }
    };

    /* ============================================================
       LOADING
       ============================================================ */
    if (loadingDevis) {
        return (
            <React.Fragment>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <div className="text-center">
                            <div className="loading-animation mb-4">
                                <div className="loading-ring"><div className="loading-ring-inner"></div></div>
                                <i className="ri-file-list-3-line loading-icon"></i>
                            </div>
                            <h5 className="fw-bold mb-2" style={{ color: COLORS.primary }}>Chargement du devis</h5>
                            <p className="text-muted small mb-0">Récupération des données...</p>
                        </div>
                    </div>
                </div>
                <Footer />
                <style>{`
                    .loading-animation { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                    .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; background: linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary}); animation: pulse-ring 1.5s ease-in-out infinite; }
                    .loading-ring-inner { position: absolute; top: 5px; left: 5px; right: 5px; bottom: 5px; border-radius: 50%; background: white; }
                    .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: ${COLORS.primary}; z-index: 2; animation: pulse-icon 1.5s ease-in-out infinite; }
                    @keyframes pulse-ring { 0% { transform: scale(0.9); opacity: 0.7; } 50% { transform: scale(1.1); opacity: 0.3; } 100% { transform: scale(0.9); opacity: 0.7; } }
                    @keyframes pulse-icon { 0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 1; } 50% { transform: translate(-50%, -50%) scale(1.1); opacity: 0.8; } }
                `}</style>
            </React.Fragment>
        );
    }

    /* ============================================================
       RENDU
       ============================================================ */
    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-file-list-3-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">
                                    {devisId ? 'Convertir devis en facture' : 'Créer une facture'}
                                </h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    {numeroFacture && (
                                        <span className="badge-modern">
                                            <i className="ri-hashtag"></i>
                                            {numeroFacture}
                                        </span>
                                    )}
                                    <span className="badge-modern" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                        <i className="ri-information-line"></i>
                                        {devisId ? 'Données pré-remplies' : 'Nouvelle facture'}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => navigate('/ventes/factures/liste')}
                            className="btn-modern-outline"
                        >
                            <i className="ri-arrow-left-line"></i> Retour à la liste
                        </Button>
                    </div>

                    <Form onSubmit={handleSubmit}>
                        <Row className="g-4">
                            {/* ===== COLONNE GAUCHE ===== */}
                            <Col lg={8}>

                                {/* Carte Informations générales */}
                                <Card className="border-0 shadow-sm rounded-3 mb-4">
                                    <Card.Header className="card-header-gradient">
                                        <h5 className="mb-0 text-white">
                                            <i className="ri-information-line me-2"></i>
                                            Informations générales
                                        </h5>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        <Row className="g-3">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Numéro de facture</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-hashtag"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            value={numeroFacture}
                                                            disabled
                                                            readOnly
                                                        />
                                                    </InputGroup>
                                                    <Form.Text className="text-muted small">
                                                        Généré automatiquement
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">
                                                        Client <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-user-line"></i></InputGroup.Text>
                                                        <Form.Select
                                                            value={facture.id_client}
                                                            onChange={(e) => setFacture({...facture, id_client: e.target.value})}
                                                            required
                                                            className="form-select-modern"
                                                        >
                                                            <option value="">Sélectionner un client</option>
                                                            {clients.map(client => (
                                                                <option key={client.id_client} value={client.id_client}>
                                                                    {client.raison_sociale} ({client.code_client})
                                                                </option>
                                                            ))}
                                                        </Form.Select>
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Date facture</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-calendar-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="date"
                                                            value={facture.date_facture}
                                                            onChange={(e) => setFacture({...facture, date_facture: e.target.value})}
                                                            required
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Date échéance</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-calendar-check-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="date"
                                                            value={facture.date_echeance}
                                                            onChange={(e) => setFacture({...facture, date_echeance: e.target.value})}
                                                            required
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Notes</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-sticky-note-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={2}
                                                            value={facture.notes}
                                                            onChange={(e) => setFacture({...facture, notes: e.target.value})}
                                                            placeholder="Notes ou observations..."
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>

                                {/* Carte Lignes */}
                                <Card className="border-0 shadow-sm rounded-3">
                                    <Card.Header className="card-header-gradient d-flex justify-content-between align-items-center flex-wrap gap-2">
                                        <h5 className="mb-0 text-white">
                                            <i className="ri-shopping-cart-line me-2"></i>
                                            Produits / Services
                                        </h5>
                                        <div className="d-flex gap-2 flex-wrap">
                                            <Button
                                                variant="light"
                                                size="sm"
                                                onClick={() => setShowServiceLibreModal(true)}
                                                className="btn-header-light"
                                            >
                                                <i className="ri-service-line me-1"></i>
                                                Service libre
                                            </Button>
                                            <Button
                                                variant="light"
                                                size="sm"
                                                onClick={() => setShowProduitModal(true)}
                                                className="btn-header-light"
                                            >
                                                <i className="ri-add-line me-1"></i>
                                                Depuis le catalogue
                                            </Button>
                                        </div>
                                    </Card.Header>
                                    <Card.Body className="p-0">
                                        {facture.lignes.length > 0 ? (
                                            <div className="table-responsive">
                                                <Table className="align-middle mb-0 table-modern">
                                                    <thead>
                                                        <tr>
                                                            <th className="ps-4">Produit / Service</th>
                                                            <th className="text-center" style={{ width: '100px' }}>Qté</th>
                                                            <th className="text-end" style={{ width: '120px' }}>Prix HT</th>
                                                            <th className="text-center" style={{ width: '100px' }}>Remise %</th>
                                                            <th className="text-end" style={{ width: '120px' }}>Total HT</th>
                                                            <th className="text-center" style={{ width: '80px' }}>TVA</th>
                                                            <th className="text-center" style={{ width: '50px' }}></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {facture.lignes.map((ligne, index) => {
                                                            const ligneIsService = ligne.type_article === 'service';
                                                            const ligneEstLibre = ligne.est_libre === true || (!ligne.id_produit && ligneIsService);
                                                            return (
                                                                <tr key={index}>
                                                                    <td className="ps-4" style={{ minWidth: '200px' }}>
                                                                        <div className="fw-semibold d-flex align-items-center gap-2 flex-wrap">
                                                                            {ligne.est_libre ? (
                                                                                <Form.Control
                                                                                    type="text"
                                                                                    value={ligne.designation}
                                                                                    onChange={(e) => updateLigne(index, 'designation', e.target.value)}
                                                                                    size="sm"
                                                                                    className="input-table"
                                                                                    style={{ minWidth: '160px' }}
                                                                                />
                                                                            ) : (
                                                                                ligne.designation
                                                                            )}
                                                                            {ligneIsService && (
                                                                                <span className="badge-type-service">
                                                                                    <i className="ri-service-line"></i> Service
                                                                                </span>
                                                                            )}
                                                                            {ligneEstLibre && (
                                                                                <span className="badge-libre" title="Service non catalogué">
                                                                                    <i className="ri-magic-line"></i> Libre
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        {!ligne.est_libre && (
                                                                            <span className="reference-badge">{ligne.reference}</span>
                                                                        )}
                                                                    </td>
                                                                    <td className="text-center">
                                                                        {ligneIsService ? (
                                                                            <span className="qte-service" title="Quantité fixée à 1 pour un service">
                                                                                1
                                                                            </span>
                                                                        ) : (
                                                                            <Form.Control
                                                                                type="number"
                                                                                min="1"
                                                                                value={ligne.quantite}
                                                                                onChange={(e) => updateLigne(index, 'quantite', parseInt(e.target.value) || 1)}
                                                                                size="sm"
                                                                                className="text-center input-table"
                                                                            />
                                                                        )}
                                                                    </td>
                                                                    <td className="text-end">
                                                                        <Form.Control
                                                                            type="number"
                                                                            step="0.01"
                                                                            value={ligne.prix_unitaire_ht}
                                                                            onChange={(e) => updateLigne(index, 'prix_unitaire_ht', parseFloat(e.target.value) || 0)}
                                                                            size="sm"
                                                                            className="text-end input-table"
                                                                        />
                                                                    </td>
                                                                    <td className="text-center">
                                                                        <Form.Control
                                                                            type="number"
                                                                            min="0"
                                                                            max="100"
                                                                            value={ligne.remise || 0}
                                                                            onChange={(e) => updateLigne(index, 'remise', parseFloat(e.target.value) || 0)}
                                                                            size="sm"
                                                                            className="text-center input-table"
                                                                        />
                                                                    </td>
                                                                    <td className="text-end fw-bold" style={{ color: COLORS.primaryDark }}>
                                                                        {formatCurrency(ligne.quantite * ligne.prix_unitaire_ht * (1 - (ligne.remise || 0)/100))}
                                                                    </td>
                                                                    <td className="text-center">
                                                                        <Badge className="badge-tva">
                                                                            {ligne.tva_taux || (taxes.tva_active ? taxes.tva_taux : 0)}%
                                                                        </Badge>
                                                                    </td>
                                                                    <td className="text-center">
                                                                        <Button
                                                                            variant="link"
                                                                            className="btn-delete-row p-0"
                                                                            onClick={() => supprimerLigne(index)}
                                                                        >
                                                                            <i className="ri-delete-bin-line"></i>
                                                                        </Button>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        ) : (
                                            <div className="empty-state-modern">
                                                <div className="empty-state-icon">
                                                    <i className="ri-shopping-cart-line"></i>
                                                </div>
                                                <p className="text-muted mb-3">Aucune ligne ajoutée</p>
                                                <div className="d-flex gap-2 justify-content-center flex-wrap">
                                                    <Button
                                                        variant="outline-primary"
                                                        onClick={() => setShowProduitModal(true)}
                                                        className="btn-modern-outline-primary"
                                                    >
                                                        <i className="ri-add-line me-2"></i>
                                                        Depuis le catalogue
                                                    </Button>
                                                    <Button
                                                        variant="outline-primary"
                                                        onClick={() => setShowServiceLibreModal(true)}
                                                        className="btn-modern-outline-primary"
                                                    >
                                                        <i className="ri-service-line me-2"></i>
                                                        Service libre
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* ===== COLONNE DROITE : RÉCAPITULATIF ===== */}
                            <Col lg={4}>
                                <Card className="border-0 shadow-sm rounded-3 sticky-top" style={{ top: '20px' }}>
                                    <Card.Header className="card-header-gradient">
                                        <h5 className="mb-0 text-white">
                                            <i className="ri-calculator-line me-2"></i>
                                            Récapitulatif
                                        </h5>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        <div className="recap-line d-flex justify-content-between mb-3">
                                            <span className="text-muted">Total HT</span>
                                            <span className="fw-bold">{formatCurrency(totaux.total_ht)}</span>
                                        </div>
                                        <div className="recap-line d-flex justify-content-between mb-3">
                                            <span className="text-muted">
                                                TVA {taxes.tva_active ? `(${taxes.tva_taux}%)` : '(inactive)'}
                                            </span>
                                            <span className="fw-bold" style={{ color: COLORS.warning }}>
                                                {formatCurrency(totaux.total_tva)}
                                            </span>
                                        </div>
                                        <div className="recap-line d-flex justify-content-between mb-3">
                                            <span className="text-muted">
                                                CSS {taxes.css_active ? `(${taxes.css_taux}%)` : '(inactive)'}
                                            </span>
                                            <span className="fw-bold" style={{ color: COLORS.info }}>
                                                {formatCurrency(totaux.total_css)}
                                            </span>
                                        </div>

                                        <div className="recap-total d-flex justify-content-between mb-4">
                                            <span className="fw-bold">Total TTC</span>
                                            <span className="fw-bold fs-5" style={{ color: COLORS.primary }}>
                                                {formatCurrency(totaux.total_ttc)}
                                            </span>
                                        </div>

                                        <div className="recap-inputs-box mb-4">
                                            <Form.Group className="mb-3">
                                                <Form.Label className="form-label-modern">Remise globale (FCFA)</Form.Label>
                                                <InputGroup className="input-modern">
                                                    <InputGroup.Text><i className="ri-percent-line"></i></InputGroup.Text>
                                                    <Form.Control
                                                        type="number"
                                                        min="0"
                                                        value={facture.remise_globale}
                                                        onChange={(e) => setFacture({...facture, remise_globale: parseFloat(e.target.value) || 0})}
                                                    />
                                                </InputGroup>
                                            </Form.Group>
                                            <Form.Group className="mb-0">
                                                <Form.Label className="form-label-modern">Avance / Acompte</Form.Label>
                                                <InputGroup className="input-modern">
                                                    <InputGroup.Text><i className="ri-hand-coin-line"></i></InputGroup.Text>
                                                    <Form.Control
                                                        type="number"
                                                        min="0"
                                                        value={facture.avance_reglement}
                                                        onChange={(e) => setFacture({...facture, avance_reglement: parseFloat(e.target.value) || 0})}
                                                    />
                                                </InputGroup>
                                            </Form.Group>
                                        </div>

                                        <div className="recap-net d-flex justify-content-between mb-4">
                                            <span className="h5 fw-bold mb-0">Net à payer</span>
                                            <span className="h5 fw-bold mb-0" style={{ color: COLORS.success }}>
                                                {formatCurrency(totaux.net_a_payer)}
                                            </span>
                                        </div>

                                        <div className="d-grid gap-2">
                                            <Button
                                                type="submit"
                                                disabled={saving}
                                                className="btn-modern-primary py-2"
                                            >
                                                {saving ? (
                                                    <>
                                                        <Spinner size="sm" className="me-2" />
                                                        Création...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="ri-save-line me-2"></i>
                                                        Créer la facture
                                                    </>
                                                )}
                                            </Button>
                                            <Button
                                                variant="outline-secondary"
                                                onClick={() => navigate('/ventes/factures/liste')}
                                                className="btn-modern-outline w-100 justify-content-center"
                                            >
                                                Annuler
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Form>
                </div>
                <Footer />
            </div>

            {/* ===== MODAL SERVICE LIBRE ===== */}
            <ModalServiceLibre
                show={showServiceLibreModal}
                onClose={() => setShowServiceLibreModal(false)}
                onAdd={ajouterServiceLibre}
                taxes={taxes}
            />

            {/* ===== MODAL CATALOGUE ===== */}
            <Modal show={showProduitModal} onHide={() => setShowProduitModal(false)} size="lg" centered className="product-modal">
                <Modal.Header closeButton className="modal-header-gradient">
                    <Modal.Title className="text-white">
                        <i className="ri-shopping-cart-line me-2"></i>
                        Sélectionner un produit ou un service
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="px-4 pb-4 pt-4">
                    <div className="table-responsive">
                        <Table hover className="align-middle mb-0 table-modern">
                            <thead>
                                <tr>
                                    <th className="ps-4">Référence</th>
                                    <th>Désignation</th>
                                    <th className="text-end">Prix HT</th>
                                    <th className="text-center">Type</th>
                                    <th className="text-center">Stock</th>
                                    <th className="pe-4"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {produits.map(produit => {
                                    const isService = produit.type_article === 'service';
                                    return (
                                        <tr key={produit.id_produit}>
                                            <td className="ps-4">
                                                <span className="reference-badge">{produit.reference}</span>
                                            </td>
                                            <td className="fw-semibold">{produit.designation}</td>
                                            <td className="text-end">{formatCurrency(produit.prix_vente_ht)}</td>
                                            <td className="text-center">
                                                {isService ? (
                                                    <span className="badge-type-service">
                                                        <i className="ri-service-line"></i> Service
                                                    </span>
                                                ) : (
                                                    <span className="badge-type-produit">
                                                        <i className="ri-box-3-line"></i> Produit
                                                    </span>
                                                )}
                                            </td>
                                            <td className="text-center">
                                                {isService ? (
                                                    <span className="text-muted small">—</span>
                                                ) : (
                                                    <span className={produit.stock_reel < produit.seuil_alerte ? 'text-danger fw-bold' : 'text-muted'}>
                                                        {produit.stock_reel || 0}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="pe-4">
                                                <Button
                                                    size="sm"
                                                    onClick={() => ajouterLigne(produit)}
                                                    className="btn-add-product"
                                                >
                                                    <i className="ri-add-line"></i>
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 pb-4 px-4">
                    <Button
                        variant="outline-secondary"
                        onClick={() => setShowProduitModal(false)}
                        className="btn-modern-outline"
                    >
                        Fermer
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                /* ===== Variables charte ===== */
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== En-tête moderne ===== */
                .header-modern {
                    background: white;
                    padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-wrap: wrap;
                    gap: 1rem;
                }
                .header-left {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                }
                .header-icon-wrapper {
                    width: 52px;
                    height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 14px;
                    color: white;
                    font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title {
                    color: #1e293b;
                    font-size: 1.3rem;
                    font-weight: 700;
                    margin: 0;
                }
                .badge-modern {
                    background: #f1f4f9;
                    color: #475569;
                    padding: 0.3rem 0.8rem;
                    border-radius: 50px;
                    font-size: 0.75rem;
                    font-weight: 600;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }

                /* ===== Header gradient ===== */
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .modal-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    padding: 1.2rem 1.5rem;
                    border: none;
                }
                .btn-header-light {
                    background: rgba(255,255,255,0.2);
                    border: 1px solid rgba(255,255,255,0.3);
                    color: white;
                    border-radius: 50px;
                    padding: 0.35rem 0.9rem;
                    font-size: 0.8rem;
                    font-weight: 500;
                    transition: all 0.2s ease;
                }
                .btn-header-light:hover {
                    background: rgba(255,255,255,0.3);
                    color: white;
                    transform: translateY(-1px);
                }

                /* ===== Inputs modernes ===== */
                .form-label-modern {
                    font-weight: 600;
                    color: #334155;
                    font-size: 0.85rem;
                    margin-bottom: 0.4rem;
                }
                .input-modern .input-group-text {
                    background: #f8fafc;
                    border: 2px solid #e2e8f0;
                    border-right: none;
                    color: var(--primary);
                    font-size: 1rem;
                    border-radius: 10px 0 0 10px;
                }
                .input-modern .form-control,
                .input-modern .form-select {
                    border: 2px solid #e2e8f0;
                    border-left: none;
                    border-radius: 0 10px 10px 0;
                    padding: 0.6rem 0.9rem;
                    font-size: 0.9rem;
                    transition: all 0.2s ease;
                }
                .input-modern .form-control:focus,
                .input-modern .form-select:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33, 172, 205, 0.1);
                }
                .form-select-modern {
                    background-color: white;
                    cursor: pointer;
                }

                /* ===== Table moderne ===== */
                .table-modern thead th {
                    background: #f8fafc;
                    color: #475569;
                    font-weight: 600;
                    font-size: 0.8rem;
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-modern tbody tr {
                    transition: background 0.15s ease;
                }
                .table-modern tbody tr:hover {
                    background: #f8fafc;
                }
                .input-table {
                    border: 1.5px solid #e2e8f0;
                    border-radius: 8px;
                    font-size: 0.85rem;
                    padding: 0.35rem 0.5rem;
                    transition: all 0.2s ease;
                }
                .input-table:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33, 172, 205, 0.1);
                }
                .reference-badge {
                    background: #f1f5f9;
                    padding: 0.2rem 0.6rem;
                    border-radius: 6px;
                    font-size: 0.7rem;
                    font-family: monospace;
                    color: #64748b;
                    display: inline-block;
                    margin-top: 2px;
                }
                .badge-tva {
                    background: #f1f5f9 !important;
                    color: #475569 !important;
                    padding: 0.35rem 0.65rem;
                    border-radius: 50px;
                    font-size: 0.75rem;
                    font-weight: 600;
                }
                .btn-delete-row {
                    color: var(--danger);
                    background: transparent;
                    border: none;
                    font-size: 1rem;
                    transition: all 0.2s ease;
                }
                .btn-delete-row:hover {
                    color: #b91c1c;
                    transform: scale(1.1);
                }

                /* ===== Badges de type ===== */
                .badge-type-service {
                    background: linear-gradient(135deg, #f0fbff, #e8f7fc);
                    color: var(--secondary);
                    padding: 0.2rem 0.6rem;
                    border-radius: 50px;
                    font-size: 0.68rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.4px;
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    border: 1px solid rgba(33, 172, 205, 0.25);
                    white-space: nowrap;
                }
                .badge-type-produit {
                    background: #f1f5f9;
                    color: #64748b;
                    padding: 0.2rem 0.6rem;
                    border-radius: 50px;
                    font-size: 0.68rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.4px;
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    border: 1px solid #e2e8f0;
                    white-space: nowrap;
                }
                .badge-libre {
                    background: linear-gradient(135deg, #fef3c7, #fde68a);
                    color: #92400e;
                    padding: 0.2rem 0.6rem;
                    border-radius: 50px;
                    font-size: 0.68rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.4px;
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    border: 1px solid rgba(245, 158, 11, 0.4);
                    white-space: nowrap;
                }

                /* ===== Quantité verrouillée pour service ===== */
                .qte-service {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    min-width: 42px;
                    padding: 0.35rem 0.5rem;
                    border-radius: 8px;
                    background: #f8fafc;
                    border: 1.5px dashed #cbd5e1;
                    color: #64748b;
                    font-weight: 700;
                    font-size: 0.85rem;
                }

                /* ===== Empty state ===== */
                .empty-state-modern {
                    text-align: center;
                    padding: 3rem 2rem;
                }
                .empty-state-icon {
                    width: 80px;
                    height: 80px;
                    background: linear-gradient(135deg, rgba(41, 90, 167, 0.08), rgba(33, 172, 205, 0.12));
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 1rem;
                    font-size: 2rem;
                    color: var(--secondary);
                }
                .btn-modern-outline-primary {
                    border: 1.5px solid var(--secondary);
                    background: white;
                    color: var(--secondary);
                    padding: 0.5rem 1.4rem;
                    border-radius: 50px;
                    font-size: 0.85rem;
                    font-weight: 600;
                    transition: all 0.2s ease;
                }
                .btn-modern-outline-primary:hover {
                    background: var(--secondary);
                    color: white;
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(33, 172, 205, 0.3);
                }

                /* ===== Récapitulatif ===== */
                .recap-line {
                    padding-bottom: 0.6rem;
                    border-bottom: 1px dashed #e2e8f0;
                    font-size: 0.9rem;
                }
                .recap-total {
                    padding-top: 0.5rem;
                    border-top: 2px solid #f1f5f9;
                }
                .recap-inputs-box {
                    background: #f8fafc;
                    padding: 1rem;
                    border-radius: 12px;
                    border: 1px solid #f1f5f9;
                }
                .recap-net {
                    padding-top: 0.75rem;
                    border-top: 2px solid #e2e8f0;
                }
                .sticky-top {
                    position: sticky;
                    top: 20px;
                    z-index: 100;
                }

                /* ===== Boutons modernes ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db;
                    background: white;
                    padding: 0.5rem 1.4rem;
                    border-radius: 50px;
                    font-size: 0.85rem;
                    font-weight: 500;
                    color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary);
                    color: var(--secondary);
                    background: #f0fbff;
                    transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33, 172, 205, 0.15);
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border: none;
                    color: white;
                    padding: 0.6rem 2rem;
                    border-radius: 50px;
                    font-weight: 600;
                    font-size: 0.9rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.3);
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41, 90, 167, 0.4);
                    color: white;
                }
                .btn-modern-primary:disabled {
                    opacity: 0.7;
                    transform: none;
                }
                .btn-add-product {
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    border: none;
                    color: white;
                    width: 34px;
                    height: 34px;
                    border-radius: 50%;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    box-shadow: 0 2px 8px rgba(41, 90, 167, 0.25);
                }
                .btn-add-product:hover {
                    transform: scale(1.1);
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.4);
                    color: white;
                }

                /* ===== Modal ===== */
                .product-modal .modal-content {
                    border: none;
                    border-radius: 16px;
                    overflow: hidden;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                }

                /* ===== Toast moderne ===== */
                .toast-notification-fixed {
                    position: fixed;
                    bottom: 24px;
                    right: 24px;
                    z-index: 9999;
                    animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white;
                    border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px;
                    max-width: 400px;
                    overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent;
                    border: none;
                    color: white;
                    cursor: pointer;
                    opacity: 0.8;
                    transition: opacity 0.2s;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p {
                    color: #1e293b;
                    font-size: 0.9rem;
                }
                .toast-progress {
                    height: 3px;
                    width: 100%;
                    animation: shrink linear forwards;
                }

                /* ===== Animations ===== */
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to   { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to   { width: 0%; }
                }

                /* ===== Responsive ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .toast-notification-fixed {
                        bottom: 12px;
                        right: 12px;
                        left: 12px;
                    }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </React.Fragment>
    );
}